import * as importedSdk from 'sharetribe-flex-sdk';
import pLimit from 'p-limit';

let exportSdk;

const isServer = () => typeof window === 'undefined';

if (isServer()) {
  // Use eval to skip webpack from bundling SDK in Node
  // eslint-disable-next-line no-eval
  exportSdk = eval('require')('sharetribe-flex-sdk');
} else {
  exportSdk = importedSdk;
}

const {
  createInstance: rawCreateInstance,
  types,
  transit,
  util,
} = exportSdk;

// ---------------------------------------------------------------------------
// GLOBAL SDK CONCURRENCY LIMITER (429 fix, 2026-09-27)
//
// Sharetribe LIVE has no request-VOLUME rate limit, but enforces a hard cap of
// 10 CONCURRENT requests per client IP across ALL environments. A heavy page
// load for a power user (slow perPage:100 sale-transactions notification query +
// the inbox-unread message pool + currentUser + assets + auth), possibly across
// multiple open tabs sharing one IP, can hold >=10 slots for several seconds.
// A Save POST (and any retries) that lands inside that window is rejected with a
// sustained 429 (the fast ~140ms rejection is the limiter counting open sockets,
// not computing anything).
//
// Fix: route EVERY sdk.* call through one shared p-limit so a single tab can
// never consume more than N of the 10 IP slots, permanently reserving headroom
// for the critical-path save + token refresh. This kills the whole CLASS of bug
// (future features can't reintroduce it) rather than patching one page.
//
// N=6 leaves >=4 slots for a second tab / the save POST / an auth-token refresh.
// Only applied client-side; SSR (Node) is single request-scoped and unaffected.
const SDK_MAX_CONCURRENCY = 6;

// EXPLICIT ALLOW-LIST of the SDK's HTTP API namespaces (each has async .query/
// .show/.update/.create/etc. that hit flex-api). We ONLY throttle these. We must
// NOT touch sdk.sdkTypes / sdk.transit (they hold UUID/Money/LatLng CLASSES —
// wrapping them would break `new sdk.sdkTypes.UUID(...)`) or any synchronous
// utility. A blanket "wrap every object prop" Proxy is unsafe for exactly that
// reason (team-reviewed, 2026-09-27).
const SDK_API_NAMESPACES = new Set([
  'currentUser',
  'transactions',
  'messages',
  'ownListings',
  'listings',
  'reviews',
  'images',
  'stripeAccount',
  'stripeCustomer',
  'stripePaymentMethods',
  'stripeSetupIntents',
  'availabilityExceptions',
  'timeslots',
  'stock',
  'stockAdjustments',
  'stockReservations',
  'bookings',
  'marketplace',
  'assetsByAlias',
  'assetsByVersion',
  'assetByAlias',
  'assetByVersion',
  'process',
  'processTransitions',
]);

// Top-level SDK methods that make HTTP calls (auth surface).
const SDK_API_TOP_LEVEL = new Set([
  'login',
  'logout',
  'authInfo',
  'exchangeToken',
  'revoke',
  'loginWithIdp',
  'currentUserShow',
]);

const wrapSdkWithLimiter = sdk => {
  if (isServer() || !sdk || typeof sdk !== 'object') return sdk;
  const limit = pLimit(SDK_MAX_CONCURRENCY);

  // Wrap only ASYNC api methods: route through the shared limiter. Bind to the
  // owning namespace so `this` is preserved.
  const wrapApiFn = (fn, ctx) => (...args) => limit(() => fn.apply(ctx, args));

  // Wrap a namespace object so each of ITS methods goes through the limiter.
  const wrapNamespace = ns =>
    new Proxy(ns, {
      get(target, prop, receiver) {
        const value = Reflect.get(target, prop, receiver);
        if (typeof value === 'function') {
          return wrapApiFn(value, target);
        }
        return value;
      },
    });

  return new Proxy(sdk, {
    get(target, prop, receiver) {
      const value = Reflect.get(target, prop, receiver);
      // Only throttle known HTTP API namespaces.
      if (
        typeof prop === 'string' &&
        SDK_API_NAMESPACES.has(prop) &&
        value &&
        typeof value === 'object'
      ) {
        return wrapNamespace(value);
      }
      // Only throttle known top-level HTTP methods.
      if (typeof prop === 'string' && SDK_API_TOP_LEVEL.has(prop) && typeof value === 'function') {
        return wrapApiFn(value, target);
      }
      // Everything else (sdkTypes, transit, config, sync utils) passes through
      // completely untouched.
      return value;
    },
  });
};

const createInstance = config => wrapSdkWithLimiter(rawCreateInstance(config));

// create image variant from variant name, desired width and aspectRatio
const createImageVariantConfig = (name, width, aspectRatio) => {
  let variantWidth = width;
  let variantHeight = Math.round(aspectRatio * width);

  if (variantWidth > 3072 || variantHeight > 3072) {
    if (!isServer) {
      console.error(`Dimensions of custom image variant (${name}) are too high (w:${variantWidth}, h:${variantHeight}).
      Reduce them to max 3072px. https://www.sharetribe.com/api-reference/marketplace.html#custom-image-variants`);
    }

    if (variantHeight > 3072) {
      variantHeight = 3072;
      variantWidth = Math.round(variantHeight / aspectRatio);
    } else if (variantHeight > 3072) {
      variantWidth = 3072;
      variantHeight = Math.round(aspectRatio * variantWidth);
    }
  }

  return {
    [`imageVariant.${name}`]: util.objectQueryString({
      w: variantWidth,
      h: variantHeight,
      fit: 'crop',
    }),
  };
};

export { createInstance, types, transit, util, createImageVariantConfig };
